import { useHookstate } from "@hookstate/core";
import { I18n } from "aws-amplify";
import { Button } from "primereact/button";
import { Card } from 'primereact/card';
import { Checkbox } from "primereact/checkbox";
import { Sidebar } from 'primereact/sidebar';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from "react";
import { useGlobalHelpState } from "../../../help-context";
import { StructureContext } from "../../../structure-context";
import { Help } from "../../help/Help";
import { currentInstallation, parseDate, scrbData, scrollToTop } from "../ScrbTools";
import CertificatsGaz from "./CertificatsGaz";
import DonneesTechniques from "./DonneesTechniques";
import Emplacement from "./Emplacement";
import Facturation from "./Facturation";
import { printView } from "./PrintInstallation";
import ReceptionGenre from "./ReceptionGenre";
import Specifications from "./Specifications";
import classNames from "classnames";

Installation.propTypes = {
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    showNotif: PropTypes.func.isRequired,
    onNouveauRdv: PropTypes.func
}

function Installation(props) {

    const structure = useContext(StructureContext);
    const installation = useHookstate(currentInstallation);
    const [currentValue, setCurrentValue] = useState();
    const data = useHookstate(scrbData).get();
    const [editable, setEditable] = useState(true);
    const helpState = useGlobalHelpState();
    const [printMode, setPrintMode] = useState(false);

    useEffect(() => {
        installation.lastUpdateBy.set("frontend");
        if (installation.get().chaudiere?.anneeConstructChaudiere) {
            installation.chaudiere.anneeEntreeChaudiere.set(new Date(Number(installation.get().chaudiere.anneeEntreeChaudiere), 0, 2));
            installation.chaudiere.anneeConstructChaudiere.set(new Date(Number(installation.get().chaudiere.anneeConstructChaudiere), 0, 2));
        }
        if (installation.get().bruleur?.anneeConstructBruleur) {
            installation.bruleur.anneeEntreeBruleur.set(new Date(Number(installation.get().bruleur.anneeEntreeBruleur), 0, 2));
            installation.bruleur.anneeConstructBruleur.set(new Date(Number(installation.get().bruleur.anneeConstructBruleur), 0, 2));
        }
        if (installation.get().anneeRenovBatiment) {
            installation.anneeRenovBatiment.set(new Date(Number(installation.get().anneeRenovBatiment), 0, 2));
        }
        scrollToTop();
        helpState.set({ show: false, help: Help.manageInstallation })
        setCurrentValue(JSON.stringify(installation.get()));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setEditable(() => {
            if (installation.get().statut) {
                return installation.get().statut < 5;
            }
            return true;
        });
        //eslint-disable-next-line
    }, [installation]);

    const onClickSave = () => {
        if (document.getElementsByClassName("p-invalid").length > 0) {
            props.showNotif("error", "Erreur", I18n.get("errorValidation"));
        }
        else {
            setEditable(false);
            props.onSave();
        }
    }

    const printPanel = () => {
        setTimeout(() => {
            const previousTitle = document.title;
            document.title = `installation-${installation.get().referenceExterne}`;
            const restoreTitle = () => {
                document.title = previousTitle;
                window.removeEventListener('afterprint', restoreTitle);
            };
            window.addEventListener('afterprint', restoreTitle);
            window.print();
            setPrintMode(false);
        }, 1000);
        return <Sidebar visible={printMode} onHide={() => setPrintMode(false)} fullScreen>
            {printView(installation.get(), data, structure)}
        </Sidebar>
    }

    const title = () => {
        const numero = installation.get().referenceExterne;
        return (
            <h5 className="flex align-items-center justify-content-between">
                <div className="flex align-items-center">
                    <i className={`mr-2 ${data.installationColors[installation.get().genreInstallationId].icon}`} style={{ fontSize: '1.5rem' }}></i>
                    <span>{numero ? `${installation.get().nom} - ${I18n.get("reference")} : ${numero} - ${parseDate(installation.get().dateDemande)}` : I18n.get("newInstallation")}</span>
                </div>
            </h5>
        );
    }

    return printMode ? printPanel() : (
        <div className="card">
            <div
                className="flex justify-content-between align-items-center border-bottom-3"
                style={{ borderImage: `linear-gradient(to right, ${data.installationColors[installation.get().genreInstallationId].color}, transparent) 1` }}
            >
                {title()}
            </div>
            <div className="flex flex-column">
                <Card>
                    <Emplacement editable={editable} />
                    <hr style={{ borderTop: "3px solid #cdcbcb" }} />
                    <Specifications editable={editable} />
                    <hr style={{ borderTop: "3px solid #cdcbcb" }} />
                    <DonneesTechniques editable={editable} />
                    <hr style={{ borderTop: "3px solid #cdcbcb" }} />
                    <ReceptionGenre editable={editable} />
                    {installation.get().genreInstallationId === 2 && (<><hr style={{ borderTop: "3px solid #cdcbcb" }} />
                        <CertificatsGaz editable={editable} /></>)}
                    <hr style={{ borderTop: "3px solid #cdcbcb" }} />
                    <Facturation editable={editable} />
                    {!installation.get().referenceExterne && <><hr style={{ borderTop: "3px solid #cdcbcb" }} />
                        <div className="flex align-items-center pt-4">
                            <Checkbox
                                inputId="conditionsUtilisation"
                                checked={installation.get().conditionsUtilisation}
                                onChange={(e) => installation.conditionsUtilisation.set(e.checked)}
                                disabled={!editable}
                                className={classNames({
                                    "p-invalid": !installation.get().conditionsUtilisation
                                })}
                            />
                            <label htmlFor="conditionsUtilisation" className="ml-2">
                                {I18n.get("jaccepte")}
                                <a href={I18n.get("linkConditionsUtilisation")} target="_blank" rel="noopener noreferrer">
                                    {I18n.get("conditionsUtilisation")}
                                </a>
                            </label>
                        </div></>}
                </Card>
            </div >
            <div className="flex justify-content-between pt-4">
                <Button
                    label={I18n.get("cancel")}
                    onClick={() => props.onCancel()}
                    icon="pi pi-arrow-left"
                    className="p-button-outlined mr-2"
                />
                {installation.get().referenceExterne && (
                    <div className="flex align-items-center">
                        <Button
                            label={I18n.get("print")}
                            icon="pi pi-print"
                            onClick={() => setPrintMode(true)}
                            className="p-button-outlined"
                        />
                        <i
                            className="pi pi-info-circle ml-2 text-blue-500"
                            style={{ cursor: 'pointer' }}
                            onClick={() => helpState.set({ show: true, help: Help.manageInstallation })}
                        />
                    </div>
                )}
                {installation.get().statut === 1 && <Button
                    label={I18n.get("nouveauRdv")}
                    icon="pi pi-calendar-plus"
                    onClick={() => props.onNouveauRdv()}
                    className="p-button-outlined"
                    disabled={currentValue !== JSON.stringify(installation.get())}
                />}
                {editable && <Button
                    label={I18n.get("save")}
                    icon="pi pi-save"
                    onClick={onClickSave}
                    className="p-button-outlined"
                    disabled={!editable || currentValue === JSON.stringify(installation.get())
                    }
                />}
            </div>
        </div >
    );
};

export default Installation;